import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileEdit, ChevronRight, Layout, Loader2, Plus, Trash2, BarChart3, Globe, X, AlertCircle, FileText, Calendar, ScrollText, UserSquare, Check, ExternalLink } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

// Icons per system page. Pages that are managed in dedicated admin sections
// (directorio, news, events, photo galleries, regulations…) also appear here
// for visibility, with their own icon.
const SYSTEM_ICONS = {
  home: '🏠',
  about: '👥',
  membership: '💳',
  resources: '📚',
  contact: '✉️',
  footer: '⚓',
  board: '🏛️',
  'past-presidents': '🎖️',
  periodicals: '📰',
  directorio: '📒',
  galeria: '🖼️',
  'fotos-ex-presidentes': '📸',
  noticias: '📰',
  eventos: '📅',
  flash: '⚡',
  estatutos: '📜',
  'reg-admisiones':  '⚖️',
  'reg-tesoreria':   '💰',
  'reg-etica':       '🛡️',
  'reg-redes':       '📨',
  'reg-malintzin':   '🏅',
  'reg-solidaridad': '🤝',
};

// Human-readable description of how each `managed_by` strategy is edited.
// Drives the secondary CTA shown on each PageCard.
const MANAGED_BY_HINT = {
  blocks:           { label: 'Editor de bloques',     short: 'Bloques' },
  interpreters:     { label: 'Admin de intérpretes',  short: 'Intérpretes' },
  'photo-galleries':{ label: 'Admin de galerías',     short: 'Galerías' },
  news:             { label: 'Admin de noticias',     short: 'Noticias' },
  events:           { label: 'Admin de eventos',      short: 'Eventos' },
  legal:            { label: 'Editor de documento legal', short: 'Documento' },
  flash:            { label: 'Editor de boletín Flash',   short: 'Flash' },
  static:           { label: 'Contenido estático',    short: 'Estático' },
};

const TEMPLATE_ICONS = {
  FileText, Calendar, ScrollText, UserSquare,
};

export default function AdminPagesPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({}); // { page_key: { views, unique } }
  const [showCreate, setShowCreate] = useState(false);
  const [feedback, setFeedback] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const [{ data: pgs }, statsRes] = await Promise.all([
        api.get('/admin/pages'),
        api.get('/admin/pages-stats-summary?days=30').catch(() => ({ data: { rows: [] } })),
      ]);
      setPages(pgs || []);
      const s = {};
      for (const r of (statsRes.data?.rows || [])) s[r.page_key] = r;
      setStats(s);
    } catch (e) {
      setPages([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function handleDelete(page) {
    if (!page.is_custom) return;
    if (!window.confirm(`¿Eliminar la página "${page.label}" y todos sus bloques? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/pages/${page.page_key}`);
      setFeedback({ type: 'success', message: 'Página eliminada' });
      await refresh();
    } catch (e) {
      setFeedback({ type: 'error', message: formatApiError(e) });
    }
  }

  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 3000);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  const systemPages = pages.filter((p) => !p.is_custom);
  const customPages = pages.filter((p) => p.is_custom);

  return (
    <div data-testid="admin-pages-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <Layout size={22} className="text-cmic-600" />
            Editor de páginas
          </h1>
          <p className="text-charcoal-500 mt-1 text-sm">
            Edita el contenido de las páginas públicas sin tocar código. Crea páginas personalizadas y ve estadísticas de visitas.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="create-custom-page-btn"
          className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm"
        >
          <Plus size={16} /> Nueva página
        </button>
      </div>

      {feedback && (
        <div className={`mb-6 px-4 py-3 rounded-xl text-sm border ${feedback.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {feedback.message}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>
      ) : (
        <>
          <h2 className="text-sm font-semibold text-charcoal-500 uppercase tracking-wider mb-3">Páginas con bloques editables</h2>
          <div className="grid gap-4 md:grid-cols-2 mb-10">
            {systemPages.filter((p) => (p.managed_by || 'blocks') === 'blocks')
              .map((p) => <PageCard key={p.page_key} page={p} stats={stats[p.page_key]} />)}
          </div>

          {systemPages.some((p) => ['interpreters','photo-galleries','news','events','legal','flash'].includes(p.managed_by)) && (
            <>
              <h2 className="text-sm font-semibold text-charcoal-500 uppercase tracking-wider mb-3">Páginas gestionadas desde otra sección</h2>
              <p className="text-xs text-charcoal-500 mb-3 -mt-1">Estas páginas obtienen su contenido de catálogos dinámicos (intérpretes, galerías, noticias, eventos) o de editores especializados (estatutos, reglamentos, boletín Flash). El botón &quot;Administrar contenido&quot; abre el editor correcto.</p>
              <div className="grid gap-4 md:grid-cols-2 mb-10">
                {systemPages.filter((p) => ['interpreters','photo-galleries','news','events','legal','flash'].includes(p.managed_by))
                  .map((p) => <PageCard key={p.page_key} page={p} stats={stats[p.page_key]} />)}
              </div>
            </>
          )}

          {systemPages.some((p) => p.managed_by === 'static') && (
            <>
              <h2 className="text-sm font-semibold text-charcoal-500 uppercase tracking-wider mb-3">Páginas estáticas (próximamente editables desde aquí)</h2>
              <p className="text-xs text-charcoal-500 mb-3 -mt-1">Estatutos, Reglamentos y Boletín Flash hoy tienen su contenido en archivos del código. Los listamos aquí para visibilidad — la migración a bloques editables es una tarea próxima.</p>
              <div className="grid gap-4 md:grid-cols-2 mb-10">
                {systemPages.filter((p) => p.managed_by === 'static')
                  .map((p) => <PageCard key={p.page_key} page={p} stats={stats[p.page_key]} />)}
              </div>
            </>
          )}

          <h2 className="text-sm font-semibold text-charcoal-500 uppercase tracking-wider mb-3">Páginas personalizadas</h2>
          {customPages.length === 0 ? (
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-2xl px-6 py-10 text-center text-sm text-charcoal-500">
              Aún no hay páginas personalizadas. Crea una con <strong>+ Nueva página</strong> para construirla desde bloques.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {customPages.map((p) => <PageCard key={p.page_key} page={p} stats={stats[p.page_key]} onDelete={() => handleDelete(p)} />)}
            </div>
          )}
        </>
      )}

      <div className="mt-10 bg-cmic-50 border border-cmic-100 text-cmic-900 text-sm rounded-2xl px-5 py-4 flex items-start gap-3">
        <FileEdit size={16} className="text-cmic-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold mb-1">Bloques disponibles:</p>
          <p className="text-cmic-800 text-xs leading-relaxed">
            <strong>Hero</strong> · <strong>Texto rico</strong> · <strong>Imagen</strong> · <strong>CTA</strong> · <strong>Tarjetas</strong> · <strong>Estadísticas</strong> · <strong>Galería</strong> · <strong>Video</strong> · <strong>FAQ/Acordeón</strong> · <strong>Testimonios</strong> · <strong>Mapa</strong> · <strong>Formulario de contacto</strong> · <strong>Lista de miembros</strong> · <strong>Espaciador</strong> · <strong>HTML libre</strong>.
          </p>
        </div>
      </div>

      {showCreate && (
        <CreatePageModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); setFeedback({ type: 'success', message: 'Página creada' }); await refresh(); }}
        />
      )}
    </div>
  );
}

function PageCard({ page, stats, onDelete }) {
  const icon = SYSTEM_ICONS[page.page_key] || '📄';
  // System pages declare their public_url + managed_by + manage_url. Custom
  // pages always use blocks and live at /es/p/{route_slug}.
  const isBlocks   = page.is_custom || page.managed_by === 'blocks';
  const publicHref = page.is_custom
    ? (page.route_slug ? `/es/p/${page.route_slug}` : null)
    : page.public_url;
  const managedHint = MANAGED_BY_HINT[page.managed_by] || MANAGED_BY_HINT.blocks;

  // For non-block pages, the primary click goes to the dedicated admin URL
  // instead of the block editor (which would do nothing).
  const primaryHref = isBlocks
    ? `/admin/pages/${page.page_key}/visual`
    : (page.manage_url || null);

  const Wrapper = primaryHref ? Link : 'div';
  const wrapperProps = primaryHref ? { to: primaryHref } : {};

  return (
    <div className="group bg-white rounded-2xl border border-gray-200 hover:shadow-md hover:border-cmic-300 transition-all" data-testid={`admin-page-card-${page.page_key}`}>
      <Wrapper
        {...wrapperProps}
        className="flex items-center gap-4 p-5"
      >
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cmic-50 to-cmic-100 flex items-center justify-center text-2xl flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-charcoal-800 group-hover:text-cmic-700 truncate flex items-center gap-2">
            {page.label_es || page.label}
            {!isBlocks && (
              <span
                className="text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5"
                title={`Esta página se edita desde: ${managedHint.label}`}
                data-testid={`admin-page-card-${page.page_key}-managed-by`}
              >
                {managedHint.short}
              </span>
            )}
          </h3>
          <p className="text-xs text-charcoal-500 mt-0.5 flex items-center gap-2 flex-wrap">
            {isBlocks ? (
              <span><span data-testid={`admin-page-card-${page.page_key}-count`}>{page.block_count}</span> bloque{page.block_count === 1 ? '' : 's'}</span>
            ) : (
              <span className="text-charcoal-500">Gestionada desde: <span className="font-medium text-charcoal-700">{managedHint.label}</span></span>
            )}
            {page.is_custom && page.route_slug && (
              <>
                <span className="text-gray-300">·</span>
                <span className="inline-flex items-center gap-1 text-cmic-600"><Globe size={11} /> /p/{page.route_slug}</span>
              </>
            )}
            {stats && stats.views > 0 && (
              <>
                <span className="text-gray-300">·</span>
                <span className="inline-flex items-center gap-1 text-charcoal-600" data-testid={`admin-page-card-${page.page_key}-views`}>
                  <BarChart3 size={11} /> {stats.views} vista{stats.views === 1 ? '' : 's'} (30d)
                </span>
              </>
            )}
          </p>
        </div>
        <ChevronRight size={18} className="text-charcoal-300 group-hover:text-cmic-500 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
      </Wrapper>
      <div className="border-t border-gray-100 px-5 py-2 flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {isBlocks ? (
            <Link
              to={`/admin/pages/${page.page_key}`}
              data-testid={`classic-editor-${page.page_key}-btn`}
              className="inline-flex items-center gap-1.5 text-xs text-charcoal-500 hover:text-cmic-600 font-medium transition-colors"
            >
              <FileEdit size={12} /> Editor clásico
            </Link>
          ) : page.manage_url ? (
            <Link
              to={page.manage_url}
              data-testid={`manage-page-${page.page_key}-btn`}
              className="inline-flex items-center gap-1.5 text-xs text-cmic-700 hover:text-cmic-800 font-semibold transition-colors"
            >
              <FileEdit size={12} /> Administrar contenido
            </Link>
          ) : (
            <span
              data-testid={`static-page-${page.page_key}-note`}
              className="inline-flex items-center gap-1.5 text-xs text-amber-700 font-medium"
              title="El contenido vive en un archivo del código. Pronto será editable desde aquí."
            >
              <AlertCircle size={12} /> Contenido en código (próximamente editable)
            </span>
          )}
          {publicHref && (
            <a
              href={publicHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`preview-page-${page.page_key}-btn`}
              title="Abrir la versión pública en una nueva pestaña"
              className="inline-flex items-center gap-1.5 text-xs text-charcoal-500 hover:text-emerald-600 font-medium transition-colors"
            >
              <ExternalLink size={12} /> Vista previa
            </a>
          )}
        </div>
        {onDelete && (
          <button
            onClick={onDelete}
            data-testid={`delete-page-${page.page_key}-btn`}
            className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 font-medium"
          >
            <Trash2 size={12} /> Eliminar
          </button>
        )}
      </div>
    </div>
  );
}

function CreatePageModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ page_key: '', label_es: '', label_en: '', route_slug: '', description: '', template_key: 'blank' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  // Track whether the user manually edited key/slug so the auto-derive keeps
  // following the title until they take over (fixes the key getting stuck on
  // the first typed character).
  const [keyEdited, setKeyEdited] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const slugify = (v) => v.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 40);

  useEffect(() => {
    api.get('/admin/page-templates').then(({ data }) => setTemplates(Array.isArray(data) ? data : [])).catch(() => setTemplates([]));
  }, []);

  // Auto-derive page_key + route_slug from the FULL label on every keystroke,
  // unless the user has manually edited those fields.
  const onLabelChange = (e) => {
    const v = e.target.value;
    setForm((f) => {
      const auto = slugify(v);
      const next = { ...f, label_es: v };
      if (!keyEdited) next.page_key = auto;
      if (!slugEdited) next.route_slug = auto;
      return next;
    });
  };

  const onKeyChange = (e) => {
    setKeyEdited(true);
    setForm((f) => ({ ...f, page_key: slugify(e.target.value) }));
  };
  const onSlugChange = (e) => {
    setSlugEdited(true);
    setForm((f) => ({ ...f, route_slug: slugify(e.target.value) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.label_es.trim()) { setError('El título en español es obligatorio'); return; }
    // Last-resort safety net: derive the key/slug from the label if empty.
    const page_key = form.page_key.trim() || slugify(form.label_es);
    const route_slug = form.route_slug.trim() || page_key;
    if (page_key.length < 2) { setError('La clave debe tener al menos 2 caracteres (solo minúsculas, números y guiones).'); return; }
    setSaving(true);
    try {
      await api.post('/admin/pages', { ...form, page_key, route_slug });
      onCreated();
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="create-page-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="font-semibold text-charcoal-800 flex items-center gap-2"><Plus size={18} className="text-cmic-600" /> Crear página personalizada</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5 overflow-y-auto">
          {/* Template picker */}
          {templates.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-2 uppercase tracking-wider">Plantilla de inicio</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="template-picker">
                {templates.map((t) => {
                  const Icon = TEMPLATE_ICONS[t.icon] || FileText;
                  const selected = form.template_key === t.key;
                  return (
                    <button
                      type="button"
                      key={t.key}
                      onClick={() => setForm((f) => ({ ...f, template_key: t.key }))}
                      data-testid={`template-${t.key}`}
                      className={`relative text-left rounded-xl border-2 px-3 py-3 transition-all ${selected ? 'border-cmic-500 bg-cmic-50/50 shadow-sm' : 'border-gray-200 hover:border-cmic-200 hover:bg-gray-50'}`}
                    >
                      {selected && (
                        <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-cmic-600 text-white flex items-center justify-center">
                          <Check size={12} />
                        </span>
                      )}
                      <Icon size={20} className={`mb-1.5 ${selected ? 'text-cmic-600' : 'text-charcoal-400'}`} />
                      <div className={`text-xs font-semibold mb-0.5 ${selected ? 'text-cmic-700' : 'text-charcoal-700'}`}>{t.label_es}</div>
                      <div className="text-[10px] text-charcoal-500 leading-tight">{t.block_count} bloque{t.block_count === 1 ? '' : 's'}</div>
                    </button>
                  );
                })}
              </div>
              {templates.find((x) => x.key === form.template_key)?.description_es && (
                <p className="text-[11px] text-charcoal-500 mt-2 italic">{templates.find((x) => x.key === form.template_key).description_es}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Título (Español) *</label>
            <input data-testid="create-page-label-es" autoFocus value={form.label_es} onChange={onLabelChange} placeholder="Ej: Preguntas frecuentes" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-cmic-500 focus:ring-1 focus:ring-cmic-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Título (English)</label>
            <input data-testid="create-page-label-en" value={form.label_en} onChange={set('label_en')} placeholder="e.g.: FAQ" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-cmic-500 focus:ring-1 focus:ring-cmic-500 outline-none" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Clave interna *</label>
              <input data-testid="create-page-key" value={form.page_key} onChange={onKeyChange} placeholder="preguntas-frecuentes" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:border-cmic-500 focus:ring-1 focus:ring-cmic-500 outline-none" />
              <p className="text-[11px] text-charcoal-500 mt-1">Solo minúsculas, números y guiones.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">URL pública</label>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-charcoal-400 text-xs">/es/p/</span>
                <input data-testid="create-page-route" value={form.route_slug} onChange={onSlugChange} placeholder="faq" className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:border-cmic-500 focus:ring-1 focus:ring-cmic-500 outline-none" />
              </div>
              <p className="text-[11px] text-charcoal-500 mt-1">Visible en la URL pública.</p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Descripción interna (opcional)</label>
            <textarea data-testid="create-page-description" value={form.description} onChange={set('description')} rows={2} placeholder="Para qué sirve esta página, notas para el equipo, etc." className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-cmic-500 focus:ring-1 focus:ring-cmic-500 outline-none" />
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 -mx-6 px-6">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
            <button type="submit" data-testid="create-page-submit-btn" disabled={saving} className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-semibold">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Crear página
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
